import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, BookOpen, Target, ListChecks, PenLine, Clock, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import InteractiveField from "@/components/InteractiveField";
import LearnBlocks from "@/components/LearnBlocks";
import { useAuth } from "@/context/AuthContext";

function Section({ icon: Icon, label, step, children }) {
  return (
    <div className="bg-white rounded-2xl p-6 card-shadow">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
          <Icon size={16} className="text-brand" />
        </div>
        <span className="overline-label">{step}. {label}</span>
      </div>
      {children}
    </div>
  );
}

function LearnCard({ item, index }) {
  if (item && typeof item === "object") {
    return (
      <div className="bg-app rounded-xl p-4" data-testid={`learn-card-${index}`}>
        <div className="font-semibold text-ink text-sm mb-1">{item.title}</div>
        <p className="text-ink-soft leading-relaxed text-sm">{item.body}</p>
      </div>
    );
  }
  return <p className="text-ink-soft leading-relaxed" data-testid={`learn-card-${index}`}>{item}</p>;
}

export default function Lesson() {
  const { day } = useParams();
  const dayNum = Number(day);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [reflect, setReflect] = useState("");
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/lessons/${dayNum}`)
      .then((r) => {
        setLesson(r.data);
        const saved = r.data.saved_answers || {};
        setReflect(saved.reflect_answer || "");
        setResponses(saved.responses || {});
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "This day isn't available yet."));
  }, [dayNum]);

  const setResp = (k, v) => setResponses((r) => ({ ...r, [k]: v }));

  const completeDay = async () => {
    setSaving(true);
    try {
      await api.post(`/lessons/${dayNum}/save`, { do_it: [], reflect_answer: reflect, responses });
      const { data } = await api.post(`/days/${dayNum}/complete`);
      await refresh();
      if (data.is_final) {
        navigate("/final");
      } else {
        navigate(`/complete/${dayNum}`, { state: data });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setSaving(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-app flex flex-col items-center justify-center px-6 text-center" data-testid="lesson-error">
        <p className="text-ink-soft">{error}</p>
        <button onClick={() => navigate("/journey")} className="mt-4 text-brand font-semibold">Back to journey</button>
      </div>
    );
  }
  if (!lesson) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin" />
      </div>
    );
  }

  const hasInteractive = lesson.interactive && lesson.interactive.length > 0;

  return (
    <div className="min-h-screen bg-app" data-testid="lesson-page">
      <div className="app-frame px-5 pt-6 pb-44">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-ink-muted mb-4" data-testid="lesson-back">
          <ChevronLeft size={20} /> Back
        </button>

        {/* header */}
        <div className="animate-fade-rise">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-accent-c font-semibold tracking-wide uppercase">{lesson.week_title}</span>
            <span className="text-ink-muted">·</span>
            <span className="text-ink-muted">{lesson.category}</span>
          </div>
          <div className="text-ink-muted text-sm font-semibold mt-3" data-testid="lesson-daycount">DAY {lesson.day} OF 30</div>
          <h1 className="font-serif text-3xl font-bold text-ink mt-1" data-testid="lesson-title">{lesson.title}</h1>
          <p className="text-ink-soft text-sm mt-1">Today's focus: {lesson.title}</p>
          {lesson.intro && <p className="text-ink-soft leading-relaxed mt-3">{lesson.intro}</p>}
          <div className="flex items-center gap-1.5 text-ink-muted text-sm mt-2">
            <Clock size={14} /> Estimated time: about {lesson.estimated_time} minutes
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <Section icon={BookOpen} label="Learn" step={1}>
            {lesson.teaching_content && (
              <div className="space-y-2 mb-4" data-testid="teaching-content">
                {lesson.teaching_content.split("\n").filter((l) => l.trim()).map((line, i) => {
                  const t = line.trim();
                  if (t.startsWith("**") && t.endsWith("**"))
                    return <h3 key={i} className="font-serif text-lg font-bold text-ink pt-1">{t.slice(2, -2)}</h3>;
                  if (t.startsWith("- "))
                    return <div key={i} className="flex gap-2 text-ink-soft"><span className="text-brand flex-shrink-0">•</span><span>{t.slice(2)}</span></div>;
                  return <p key={i} className="text-ink-soft leading-relaxed">{t}</p>;
                })}
              </div>
            )}
            {lesson.key_takeaways && lesson.key_takeaways.length > 0 && (
              <div className="bg-sage/60 border border-subtle rounded-2xl p-4 mb-4" data-testid="key-takeaways">
                <p className="overline-label mb-2">Key takeaways</p>
                <ul className="space-y-1.5">
                  {lesson.key_takeaways.map((k, i) => (
                    <li key={i} className="flex gap-2 text-ink-soft text-sm"><span className="text-brand flex-shrink-0">✓</span>{k}</li>
                  ))}
                </ul>
              </div>
            )}
            {lesson.learn_blocks && lesson.learn_blocks.length > 0 ? (
              <LearnBlocks blocks={lesson.learn_blocks} />
            ) : (
              <div className="space-y-3">
                {lesson.learn.map((item, i) => (
                  <LearnCard key={i} item={item} index={i} />
                ))}
              </div>
            )}
          </Section>

          <Section icon={Target} label="Today's Action" step={2}>
            <p className="text-ink text-lg leading-relaxed font-medium">{lesson.action}</p>
          </Section>

          {hasInteractive && (
            <Section icon={ListChecks} label="Check In" step={3}>
              <div className="space-y-5">
                {lesson.interactive.map((f) => (
                  <InteractiveField
                    key={f.key}
                    field={f}
                    value={responses[f.key]}
                    onChange={(v) => setResp(f.key, v)}
                  />
                ))}
              </div>
            </Section>
          )}

          <Section icon={PenLine} label="Reflect" step={hasInteractive ? 4 : 3}>
            <p className="text-ink font-medium mb-3">{lesson.reflect}</p>
            <textarea
              data-testid="reflect-input"
              value={reflect}
              onChange={(e) => setReflect(e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)] resize-none"
              placeholder="Take a moment to write your thoughts…"
            />
          </Section>
        </div>
      </div>

      {/* sticky complete */}
      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-xl border-t border-subtle" style={{ backgroundColor: "rgba(250,248,245,0.92)" }}>
        <div className="max-w-[480px] mx-auto px-5 py-4">
          <button
            data-testid="complete-day-btn"
            onClick={completeDay}
            disabled={saving}
            className="w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-4 rounded-full flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {saving ? "Saving…" : (<><Check size={17} /> {lesson.button_label || "Complete Day"}</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
